import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Use sessionStorage instead of localStorage - token clears when browser/tab closes
  const [token, setToken] = useState(sessionStorage.getItem("authToken"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const validateToken = async () => {
      // Also clear any old localStorage tokens (migration cleanup)
      localStorage.removeItem("authToken");
      localStorage.removeItem("token");
      
      // Check sessionStorage for token on app start
      const storedToken = sessionStorage.getItem("authToken");
      
      if (storedToken) {
        try {
          const res = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(res.data);
          setToken(storedToken);
        } catch (err) {
          // Token invalid, clear it from sessionStorage
          sessionStorage.removeItem("authToken");
          setToken(null);
          setUser(null);
        }
      } else {
        // No token in sessionStorage - user must login
        setToken(null);
        setUser(null);
      }
      setLoading(false);
    };
    validateToken();
  }, []);

  const login = async (username, password) => {
    const res = await axios.post(`${API}/auth/login`, { username, password });
    const { token: newToken, username: uname, name } = res.data;
    
    // Store token in sessionStorage ONLY (not localStorage)
    // This ensures user must login when browser/tab is closed and reopened
    sessionStorage.setItem("authToken", newToken);
    
    setToken(newToken);
    
    // Fetch full user info including allowed_pages
    try {
      const userRes = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${newToken}` }
      });
      setUser(userRes.data);
    } catch (err) {
      setUser({ username: uname, name });
    }
    
    return res.data;
  };

  const logout = async () => {
    try {
      const currentToken = sessionStorage.getItem("authToken");
      if (currentToken) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });
      }
    } catch (err) {
      // Ignore logout errors
    }
    
    // Clear token from sessionStorage
    sessionStorage.removeItem("authToken");
    setToken(null);
    setUser(null);
    
    // Redirect to login page
    window.location.href = "/login";
  };

  // Check if user has access to a specific page
  const hasPageAccess = (path) => {
    if (!user) return false;
    // If no allowed_pages defined, user has access to all pages (admin)
    if (!user.allowed_pages) return true;
    // Check if the path is in allowed_pages
    return user.allowed_pages.includes(path);
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user && !!token,
    login,
    logout,
    hasPageAccess
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
